import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { getErrorMessage } from '@/shared/api/api-error';

import {
  createCategory,
  deleteCategory,
  getCategories,
  getCategoryOptions,
  getCategoryOrder,
  saveCategoryOrder,
  setCategoryStatus,
  updateCategory,
} from './categories.api';
import type {
  CategoryListParams,
  CreateCategoryPayload,
  UpdateCategoryPayload,
} from '../types/category.types';

/**
 * Query keys. Every key starts with `['categories']`; a change invalidates the
 * whole feature (lists and picker options) and the brands feature, whose rows
 * carry the category name and its active state.
 */
export const categoryKeys = {
  all: ['categories'] as const,
  lists: () => [...categoryKeys.all, 'list'] as const,
  list: (params: CategoryListParams) => [...categoryKeys.lists(), params] as const,
  options: () => [...categoryKeys.all, 'options'] as const,
  order: () => [...categoryKeys.all, 'order'] as const,
};

/** The brands feature's root key — kept literal to avoid importing that feature. */
const BRANDS_KEY = ['brands'] as const;

export function useCategories(params: CategoryListParams) {
  return useQuery({
    queryKey: categoryKeys.list(params),
    queryFn: ({ signal }) => getCategories(params, signal),
    placeholderData: (previous) => previous,
  });
}

/**
 * Category options for pickers. Long `staleTime`: the list is small and
 * curated; changes made here invalidate it.
 */
export function useCategoryOptions() {
  return useQuery({
    queryKey: categoryKeys.options(),
    queryFn: ({ signal }) => getCategoryOptions(signal),
    staleTime: 15 * 60_000,
  });
}

function useInvalidateCategories() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: categoryKeys.all });
    void queryClient.invalidateQueries({ queryKey: BRANDS_KEY });
  };
}

export function useCreateCategory() {
  const invalidate = useInvalidateCategories();
  return useMutation({
    mutationFn: (payload: CreateCategoryPayload) => createCategory(payload),
    onSuccess: (_id, payload) => {
      toast.success(`Category "${payload.nameEn}" created.`);
      invalidate();
    },
    // Field errors (a name in use) are mapped onto the form by the caller.
  });
}

export function useUpdateCategory() {
  const invalidate = useInvalidateCategories();
  return useMutation({
    mutationFn: ({ categoryId, payload }: { categoryId: number; payload: UpdateCategoryPayload }) =>
      updateCategory(categoryId, payload),
    onSuccess: (_result, { payload }) => {
      toast.success(`Category "${payload.nameEn}" updated.`);
      invalidate();
    },
  });
}

/** No optimistic update: deactivating a category changes what customers see. */
export function useSetCategoryStatus() {
  const invalidate = useInvalidateCategories();
  return useMutation({
    mutationFn: (input: { categoryId: number; isActive: boolean; reason: string | null }) =>
      setCategoryStatus(input.categoryId, input.isActive, input.reason),
    onSuccess: (changed, { isActive }) => {
      toast.success(
        changed === 0
          ? 'Nothing to change.'
          : isActive
            ? 'Category activated.'
            : 'Category deactivated.',
      );
      invalidate();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Could not change the category.'));
    },
  });
}

/** Only an unused category can be deleted; the API's refusal is shown as a toast. */
export function useDeleteCategory() {
  const invalidate = useInvalidateCategories();
  return useMutation({
    mutationFn: (input: { categoryId: number; name: string; reason: string | null }) =>
      deleteCategory(input.categoryId, input.reason),
    onSuccess: (_result, { name }) => {
      toast.success(`Category "${name}" deleted.`);
      invalidate();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Could not delete the category.'));
      // A 409 means brands were added meanwhile — refresh the counts.
      invalidate();
    },
  });
}

export function useCategoryOrder(enabled: boolean) {
  return useQuery({
    queryKey: categoryKeys.order(),
    queryFn: ({ signal }) => getCategoryOrder(signal),
    enabled,
  });
}

/** Errors (an incomplete order) are shown in the dialog by the caller. */
export function useSaveCategoryOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ categoryIds, reason }: { categoryIds: number[]; reason: string }) =>
      saveCategoryOrder(categoryIds, reason),
    onSuccess: (changed) => {
      toast.success(changed === 0 ? 'The order was already saved.' : 'Storefront order saved.');
      void queryClient.invalidateQueries({ queryKey: categoryKeys.all });
    },
    // An incomplete list means a category was added or removed meanwhile — reload it.
    onError: () => void queryClient.invalidateQueries({ queryKey: categoryKeys.order() }),
  });
}
